/**
 * Permission gate. Wrap a route or component to require one or more
 * permissions / roles before render. On miss → 403 fallback or redirect
 * to /login if unauthenticated.
 *
 * On mount, validates the stored session by calling /api/auth/me.
 * If the JWT is expired and can't be refreshed, clears the session
 * and redirects to /login.
 */

import { useEffect, useRef, type ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { request } from '@/api/client'

interface Props {
  children: ReactNode
  /** Single permission required. */
  requires?: string
  /** Any-of permissions (OR). */
  requiresAny?: string[]
  /** All-of permissions (AND). */
  requiresAll?: string[]
  /** Specific role required (e.g., "admin"). */
  requiresRole?: string
  /** Custom fallback when authenticated but lacking permission. */
  fallback?: ReactNode
}

export function RoleGate({
  children,
  requires,
  requiresAny,
  requiresAll,
  requiresRole,
  fallback,
}: Props) {
  const { isAuthenticated, hasPerm, hasRole } = useAuth()
  const location = useLocation()
  const validated = useRef(false)

  // Validate session on mount — if the stored JWT is expired, the
  // request() function will attempt a refresh. If that also fails,
  // it clears the session and redirects to /login.
  useEffect(() => {
    if (!isAuthenticated || validated.current) return
    validated.current = true
    request('/api/auth/me').catch(() => {
      // request() handles 401 → refresh → logout internally
    })
  }, [isAuthenticated])

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />
  }

  let ok = true
  if (requires) ok = ok && hasPerm(requires)
  if (requiresRole) ok = ok && hasRole(requiresRole)
  if (requiresAll && requiresAll.length) {
    ok = ok && requiresAll.every((p) => hasPerm(p))
  }
  if (requiresAny && requiresAny.length) {
    ok = ok && requiresAny.some((p) => hasPerm(p))
  }

  if (!ok) {
    return (
      <>
        {fallback ?? (
          <div className="p-12 text-center text-ink-500">
            <h1 className="text-xl font-semibold text-ink-900 mb-2">
              Access denied
            </h1>
            <p className="text-sm">
              You don&apos;t have permission to view this page.
            </p>
          </div>
        )}
      </>
    )
  }

  return <>{children}</>
}
